import React, { useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  Button,
  IconButton,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { makeStyles } from "@mui/styles";
import dayjs from "dayjs";
import clsx from "clsx";
import { IconGoLink } from "../../../../components/SectionGoButtons";
import {
  TASK_REPORT_STATUS,
  TASK_REPORT_STATUS_OPTIONS,
  formatTaskInfoLabel,
  getDocumentsHref,
  isDateInPeriods,
} from "../../helpers";

const WEEKDAY_LABELS = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];

const ClearIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M5 15L15 5M5 5L15 15"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const createCalendarDay = (periods) => (props) => {
  const { day, outsideCurrentMonth, selected, ...other } = props;
  const isOutside = Boolean(outsideCurrentMonth);
  const inPeriod = !isOutside && isDateInPeriods(day, periods);
  const prevInPeriod = !isOutside && isDateInPeriods(day.subtract(1, "day"), periods);
  const nextInPeriod = !isOutside && isDateInPeriods(day.add(1, "day"), periods);
  const weekday = day.day();
  const roundLeft = inPeriod && (weekday === 1 || !prevInPeriod);
  const roundRight = inPeriod && (weekday === 0 || !nextInPeriod);

  return (
    <Box
      sx={{
        flex: "1 1 0",
        width: "14.2857%",
        maxWidth: "14.2857%",
        height: 32,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: inPeriod ? "#E0E7FF" : "transparent",
        borderTopLeftRadius: roundLeft ? 16 : 0,
        borderBottomLeftRadius: roundLeft ? 16 : 0,
        borderTopRightRadius: roundRight ? 16 : 0,
        borderBottomRightRadius: roundRight ? 16 : 0,
      }}
    >
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        selected={selected}
        disableMargin
        sx={{
          width: 32,
          height: 32,
          margin: 0,
          fontSize: 14,
          fontWeight: 400,
          lineHeight: "20px",
          color: isOutside ? "#A1A1AA" : "#111111",
          backgroundColor: "transparent",
          borderRadius: "50%",
          "&:hover": {
            backgroundColor: "rgba(97, 95, 255, 0.16)",
          },
          "&.Mui-selected": {
            backgroundColor: "#615FFF",
            color: "#FFFFFF",
            fontWeight: 500,
            "&:hover": {
              backgroundColor: "#4F46E5",
            },
            "&:focus": {
              backgroundColor: "#615FFF",
            },
          },
          "&.MuiPickersDay-today": {
            border: "none",
            fontWeight: selected ? 500 : 400,
          },
          "&.MuiPickersDay-dayOutsideMonth": {
            color: "#A1A1AA",
          },
        }}
      />
    </Box>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  title: {
    margin: 0,
    fontSize: 24,
    fontWeight: 500,
    lineHeight: "28px",
    color: "#111111",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    minHeight: 44,
  },
  statusRow: {
    width: "100%",
  },
  statusInput: {
    "& .MuiOutlinedInput-root": {
      height: 44,
      minHeight: 44,
      backgroundColor: "#FFFFFF",
      display: "flex",
      alignItems: "center",
      // Prevent chips/tags from wrapping to a second line (height expansion).
      flexWrap: "nowrap",
      overflow: "hidden",
      "& input": {
        padding: "0 !important",
        margin: 0,
      },
    },
    // MUI Autocomplete tags are placed inside the input root; override there too.
    "& .MuiAutocomplete-inputRoot": {
      flexWrap: "nowrap !important",
      overflow: "hidden",
    },
    "& .MuiChip-label": {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "&.--floating-label .MuiFormLabel-root:not(.MuiInputLabel-shrink)": {
      top: "50%",
      transform: "translate(12px, -50%) scale(1)",
    },
    "&.--floating-label .MuiFormLabel-root.MuiInputLabel-shrink": {
      top: 0,
      transform: "translate(12px, 4px) scale(0.75)",
    },
  },
  dateField: {
    position: "relative",
    width: 344,
    height: 44,
    minHeight: 44,
    maxHeight: 44,
    padding: "0 36px 6px 8px",
    border: "1px solid #E4E4E7",
    borderRadius: 4,
    backgroundColor: "#FFFFFF",
    boxSizing: "border-box",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    outline: "none",
    fontFamily: "inherit",
    textAlign: "left",
    "&:hover": {
      borderColor: "#E4E4E7",
    },
  },
  dateLabel: {
    position: "absolute",
    top: 0,
    left: 8,
    height: 24,
    fontSize: 12,
    lineHeight: "24px",
    color: "#A1A1AA",
    pointerEvents: "none",
  },
  dateLabelEmpty: {
    top: "50%",
    transform: "translateY(-50%)",
    height: "auto",
    fontSize: 14,
    lineHeight: "20px",
  },
  dateValue: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "16px",
    color: "#111111",
  },
  clearButton: {
    position: "absolute",
    top: "50%",
    right: 8,
    transform: "translateY(-50%)",
    padding: 4,
    color: "#111111",
    "&:hover": {
      backgroundColor: "transparent",
      color: "#111111",
    },
  },
  taskInfo: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minHeight: 44,
  },
  taskText: {
    fontSize: 14,
    lineHeight: "20px",
    fontWeight: 400,
    color: "#71717A",
    whiteSpace: "nowrap",
  },
  statusFilterWrap: {
    width: 344,
    maxWidth: "100%",
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 12px",
    borderRadius: 20,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.1,
  },
  statusPillMatched: {
    backgroundColor: "#DCFCE7",
    color: "#116932",
  },
  statusPillTaskOnly: {
    backgroundColor: "#FEE2E2",
    color: "#991919",
  },
  statusPillDailyOnly: {
    backgroundColor: "#FEF9C3",
    color: "#713F12",
  },
  statusPillConflict: {
    backgroundColor: "#DBEAFE",
    color: "#173DA6",
  },
  statusOptionText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  calendarPaper: {
    width: 328,
    boxSizing: "border-box",
    border: "1px solid #E4E4E7",
    borderRadius: 8,
    boxShadow: "0px 4px 8px rgba(24, 24, 27, 0.1)",
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
  },
  calendar: {
    width: "100% !important",
    maxHeight: "none !important",
    overflow: "hidden",
    "& .MuiPickersCalendarHeader-root": {
      margin: 0,
      padding: "16px",
      minHeight: 68,
      borderBottom: "1px solid #E4E4E7",
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    "& .MuiPickersCalendarHeader-labelContainer": {
      margin: 0,
      position: "relative",
      zIndex: 1,
      maxHeight: "none",
    },
    "& .MuiPickersCalendarHeader-label": {
      fontSize: 16,
      fontWeight: 500,
      lineHeight: "24px",
      color: "#18181B",
      textTransform: "capitalize",
    },
    "& .MuiPickersCalendarHeader-switchViewButton": {
      marginRight: 0,
      padding: 4,
      color: "#71717A",
    },
    "& .MuiPickersArrowSwitcher-root": {
      position: "absolute",
      left: 16,
      right: 16,
      top: "50%",
      transform: "translateY(-50%)",
      display: "flex",
      justifyContent: "space-between",
      width: "auto",
      pointerEvents: "none",
    },
    "& .MuiPickersArrowSwitcher-button": {
      pointerEvents: "auto",
      width: 36,
      height: 36,
      border: "1px solid #E4E4E7",
      borderRadius: 4,
      color: "#71717A",
      padding: 0,
      backgroundColor: "#FFFFFF",
      "&:hover": {
        backgroundColor: "#F4F4F5",
      },
    },
    "& .MuiDateCalendar-viewTransitionContainer": {
      overflow: "hidden !important",
    },
    "& .MuiDayCalendar-header": {
      display: "flex",
      justifyContent: "stretch",
      padding: "12px 16px 0",
      margin: 0,
    },
    "& .MuiDayCalendar-weekDayLabel": {
      flex: "1 1 0",
      width: "auto",
      height: 32,
      margin: 0,
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "32px",
      color: "#71717A",
    },
    "& .MuiDayCalendar-monthContainer": {
      padding: "0 16px 8px",
      overflow: "hidden",
    },
    "& .MuiDayCalendar-weekContainer": {
      margin: 0,
      display: "flex",
      justifyContent: "stretch",
      minHeight: 32,
      gap: 0,
    },
    "& .MuiPickersSlideTransition-root": {
      minHeight: "192px !important",
      overflowX: "hidden !important",
      overflowY: "hidden !important",
    },
    "& .MuiDayCalendar-slideTransition": {
      minHeight: "192px !important",
      overflow: "hidden !important",
    },
  },
  calendarActions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
    padding: "16px",
    borderTop: "1px solid #E4E4E7",
  },
  cancelBtn: {
    height: 36,
    minWidth: 0,
    textTransform: "none",
    backgroundColor: "#E0E7FF",
    color: "#432DD7",
    fontWeight: 500,
    fontSize: 14,
    borderRadius: 4,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#C7D2FE",
      boxShadow: "none",
    },
  },
  applyBtn: {
    height: 36,
    minWidth: 0,
    textTransform: "none",
    backgroundColor: "#4F39F6",
    color: "#FFFFFF",
    fontWeight: 500,
    fontSize: 14,
    borderRadius: 4,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#4330D4",
      boxShadow: "none",
    },
  },
}));

const Filter = ({ filter, taskMeta, periods = [], onChange }) => {
  const classes = useStyles();
  const fieldRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [draftDate, setDraftDate] = useState(null);
  const CalendarDay = useMemo(() => createCalendarDay(periods), [periods]);

  const taskInfoLabel = formatTaskInfoLabel(taskMeta);
  const documentsHref = getDocumentsHref(taskMeta);
  const dateValue = filter.daily_date ? dayjs(filter.daily_date) : null;
  const hasValidDate = Boolean(dateValue?.isValid?.());
  const dateLabel = hasValidDate ? dateValue.format("DD.MM.YYYY") : "";
  const selectedStatuses = TASK_REPORT_STATUS_OPTIONS.filter((option) =>
    (filter.status || []).includes(option.value)
  );

  const getStatusPillClassName = (status) => {
    if (status === TASK_REPORT_STATUS.MATCHED) return classes.statusPillMatched;
    if (status === TASK_REPORT_STATUS.TASK_ONLY) return classes.statusPillTaskOnly;
    if (status === TASK_REPORT_STATUS.DAILY_ONLY) return classes.statusPillDailyOnly;
    if (status === TASK_REPORT_STATUS.CONFLICT) return classes.statusPillConflict;
    return null;
  };

  const getStatusPillColors = (status) => {
    switch (status) {
      case TASK_REPORT_STATUS.MATCHED:
        return { backgroundColor: "#DCFCE7", color: "#116932" };
      case TASK_REPORT_STATUS.TASK_ONLY:
        return { backgroundColor: "#FEE2E2", color: "#991919" };
      case TASK_REPORT_STATUS.DAILY_ONLY:
        return { backgroundColor: "#FEF9C3", color: "#713F12" };
      case TASK_REPORT_STATUS.CONFLICT:
        return { backgroundColor: "#DBEAFE", color: "#173DA6" };
      default:
        return null;
    }
  };

  const applyDate = (nextValue) => {
    const iso = nextValue && nextValue.isValid()
      ? nextValue.startOf("day").toISOString()
      : null;
    onChange({
      ...filter,
      page: 1,
      daily_date: iso,
    }, true);
  };

  const handleOpen = () => {
    setDraftDate(hasValidDate ? dateValue : dayjs());
    setOpen(true);
  };

  const handleClear = (event) => {
    event.preventDefault();
    event.stopPropagation();
    applyDate(null);
  };

  const handleCancel = () => setOpen(false);

  const handleApply = () => {
    applyDate(draftDate);
    setOpen(false);
  };

  const handleStatusChange = (_event, values) => {
    onChange({
      ...filter,
      page: 1,
      status: (values || []).map((item) => item.value),
    }, true);
  };

  return (
    <Box className={classes.root}>
      <h1 className={classes.title}>Задания</h1>

      <Box className={classes.row}>
        <Box className={classes.statusFilterWrap}>
          <Autocomplete
            multiple
            // Keep the input height stable: show only the first chip and "+N" for the rest.
            limitTags={1}
            options={TASK_REPORT_STATUS_OPTIONS}
            value={selectedStatuses}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onChange={handleStatusChange}
            renderOption={(props, option) => {
              const pillClassName = getStatusPillClassName(option.value);
              return (
                <Box component="li" {...props}>
                  {pillClassName ? (
                    <span className={clsx(classes.statusPill, pillClassName)}>{option.label}</span>
                  ) : (
                    <span className={classes.statusOptionText}>{option.label}</span>
                  )}
                </Box>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const pillClassName = getStatusPillClassName(option.value);
                const tagProps = getTagProps({ index });
                const colors = getStatusPillColors(option.value);
                return (
                  <Chip
                    {...tagProps}
                    key={option.value}
                    label={option.label}
                    size="small"
                    className={clsx(tagProps.className, pillClassName)}
                    sx={{
                      height: 24,
                      borderRadius: "20px",
                      fontSize: 14,
                      fontWeight: 500,
                      ...(colors ? { backgroundColor: colors.backgroundColor, color: colors.color } : null),
                      "& .MuiChip-deleteIcon": {
                        color: "inherit",
                      },
                      "&:hover": { backgroundColor: colors?.backgroundColor },
                    }}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Выполнение задания"
                variant="outlined"
                className={`${classes.statusInput} --floating-label`}
              />
            )}
          />
        </Box>

        <button
          ref={fieldRef}
          type="button"
          className={classes.dateField}
          onClick={handleOpen}
        >
          <span
            className={`${classes.dateLabel} ${!hasValidDate ? classes.dateLabelEmpty : ""}`}
          >
            Дата
          </span>
          {hasValidDate ? <span className={classes.dateValue}>{dateLabel}</span> : null}
          {hasValidDate ? (
            <IconButton
              type="button"
              size="small"
              className={classes.clearButton}
              aria-label="Очистить дату"
              onClick={handleClear}
            >
              <ClearIcon />
            </IconButton>
          ) : null}
        </button>

        {taskInfoLabel ? (
          <Box className={classes.taskInfo}>
            <Typography className={classes.taskText}>{taskInfoLabel}</Typography>
            {documentsHref ? <IconGoLink href={documentsHref} visible /> : null}
          </Box>
        ) : null}
      </Box>

      <Popover
        open={open}
        anchorEl={fieldRef.current}
        onClose={handleCancel}
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: { className: classes.calendarPaper, elevation: 0 },
          backdrop: { sx: { backgroundColor: "transparent" } },
        }}
      >
        <DateCalendar
          value={draftDate}
          onChange={(next) => setDraftDate(next)}
          className={classes.calendar}
          reduceAnimations
          showDaysOutsideCurrentMonth
          dayOfWeekFormatter={(date) => WEEKDAY_LABELS[date.day()]}
          slots={{ day: CalendarDay }}
          slotProps={{
            calendarHeader: {
              format: "MMMM YYYY",
            },
          }}
        />
        <Box className={classes.calendarActions}>
          <Button className={classes.cancelBtn} onClick={handleCancel}>
            Отменить
          </Button>
          <Button variant="contained" className={classes.applyBtn} onClick={handleApply}>
            Применить
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default Filter;
